import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

interface SiteHeaderProps {
  overlay?: boolean;
}

const navItems = [
  { to: "/", label: "首页" },
  { to: "/works", label: "影视作品" },
  { to: "/news", label: "动态资讯" },
  { to: "/profile", label: "个人资料" },
  { to: "/about", label: "关于" },
];

export default function SiteHeader({ overlay = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const solid = !overlay || scrolled || menuOpen;

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    setMenuOpen(false);
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
          solid
            ? "border-b border-background-200/80 bg-background-50/95 backdrop-blur-md"
            : "border-b border-transparent bg-gradient-to-b from-primary-950/60 to-transparent"
        }`}
      >
        <div className="container-page flex h-16 items-center justify-between gap-3 md:h-20 md:gap-8">
          <Link to="/" className="group flex shrink-0 cursor-pointer items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-600 font-heading text-lg text-background-50 transition-colors group-hover:bg-primary-700 md:h-10 md:w-10">
              翔
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={`font-heading text-[17px] tracking-wide md:text-lg ${solid ? "text-foreground-950" : "text-background-50"}`}
              >
                翔田千里
              </span>
              <span
                className={`mt-1 text-[9px] uppercase tracking-wider2 ${solid ? "text-foreground-400" : "text-background-50/60"}`}
              >
                Chisato Shoda
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="主导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `relative cursor-pointer whitespace-nowrap pb-1 text-sm transition-colors ${
                    isActive
                      ? solid
                        ? "text-accent-600"
                        : "text-accent-300"
                      : solid
                        ? "text-foreground-700 hover:text-primary-700"
                        : "text-background-50/80 hover:text-background-50"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="relative flex flex-col items-center">
                    {item.label}
                    <span
                      className={`absolute -bottom-1.5 h-0.5 w-4 rounded-full transition-opacity ${
                        isActive ? "bg-accent-500 opacity-100" : "opacity-0"
                      }`}
                    ></span>
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <form
              onSubmit={handleSearch}
              className={`hidden items-center gap-2 rounded-md border px-3 py-2 transition-colors md:flex ${
                solid
                  ? "border-background-300/80 bg-background-100 text-foreground-600 focus-within:border-primary-300"
                  : "border-background-50/25 bg-background-50/10 text-background-50/80 backdrop-blur"
              }`}
            >
              <i className="ri-search-line text-base" aria-hidden="true"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索作品 / 资讯"
                aria-label="全站搜索"
                className={`w-32 bg-transparent text-sm outline-none lg:w-40 ${
                  solid
                    ? "text-foreground-800 placeholder:text-foreground-400"
                    : "text-background-50 placeholder:text-background-50/60"
                }`}
              />
              <button type="submit" className="sr-only">
                搜索
              </button>
            </form>

            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
              aria-expanded={menuOpen}
              className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border text-xl transition-colors lg:hidden ${
                solid
                  ? "border-background-300/80 bg-background-50 text-foreground-800"
                  : "border-background-50/25 bg-background-50/10 text-background-50 backdrop-blur"
              }`}
            >
              <i className={menuOpen ? "ri-close-line" : "ri-menu-line"} aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-40 overflow-y-auto bg-background-50 px-5 pb-10 pt-20 lg:hidden">
          <form
            onSubmit={handleSearch}
            className="flex items-center gap-2 rounded-md border border-background-300/80 bg-background-100 px-3 py-2.5"
          >
            <i className="ri-search-line text-lg text-foreground-500" aria-hidden="true"></i>
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索作品名、类型或关键字"
              aria-label="全站搜索"
              className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="flex shrink-0 items-center justify-center rounded bg-primary-600 px-3 py-1.5 text-xs text-background-50"
            >
              搜索
            </button>
          </form>

          <nav className="mt-6 flex flex-col" aria-label="移动端主导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `flex items-center justify-between border-b border-background-200/80 py-4 font-heading text-lg transition-colors ${
                    isActive ? "text-accent-600" : "text-foreground-900"
                  }`
                }
              >
                {item.label}
                <i className="ri-arrow-right-up-line text-base text-foreground-400" aria-hidden="true"></i>
              </NavLink>
            ))}
          </nav>

          <div className="mt-6 rounded-lg bg-primary-950 p-5 text-background-50">
            <p className="font-heading text-base">作品与资讯索引</p>
            <p className="mt-2 text-xs leading-relaxed text-background-50/70">
              收录翔田千里 2010 年至今的电影、电视剧作品，以及访谈、活动与出版动态。
            </p>
            <Link
              to="/works"
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2 text-xs font-medium text-primary-950"
            >
              浏览全部作品
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      )}
    </>
  );
}